/**
 * useVehicleActivityAttachments - Agregação de anexos de um veículo
 *
 * Lista TODOS os anexos vinculados ao veículo — anexos diretos e anexos
 * de andamentos (vehicle_notes). Usado pela aba "Anexos" do modal de
 * veículos. Paginação server-side via COUNT(*) OVER() na RPC, com filtro
 * opcional por tipo de arquivo.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-08 v1.0.0 - Versão inicial. Espelha
 *            useTaskActivityAttachments trocando task → vehicle.
 */
import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

const logger = createLogger('VehicleActivityAttachments');

/** Origem do anexo dentro do veículo */
export type VehicleAttachmentSourceType = 'vehicle' | 'note';

/** Item retornado pela RPC */
export interface VehicleActivityAttachment {
  id: string;
  source_type: VehicleAttachmentSourceType;
  source_id: string;
  source_title: string;
  source_created_at: string;
  file_name: string;
  file_type: string;
  mime_type: string | null;
  file_size: number | null;
  storage_path: string;
  public_url: string;
  thumbnail_url: string | null;
  uploaded_by: string | null;
  uploaded_by_name: string | null;
  uploaded_by_avatar: string | null;
  created_at: string;
}

/** Filtro por tipo (mesmos valores da classificação da RPC) */
export type VehicleAttachmentTypeFilter =
  | 'all'
  | 'image'
  | 'pdf'
  | 'document'
  | 'spreadsheet'
  | 'other';

interface Options {
  /** Habilita a query (padrão true) — usado para lazy loading da aba */
  enabled?: boolean;
  /** Tamanho da página (padrão 60) */
  pageSize?: number;
}

/**
 * Hook principal — retorna anexos paginados + contagem total
 */
export function useVehicleActivityAttachments(
  vehicleId: string | null | undefined,
  typeFilter: VehicleAttachmentTypeFilter = 'all',
  options?: Options,
) {
  const { currentTenant } = useAuth();
  const pageSize = options?.pageSize ?? 60;
  const isEnabled = (options?.enabled ?? true) && !!vehicleId && !!currentTenant?.id;

  const query = useInfiniteQuery({
    queryKey: ['vehicle-activity-attachments', vehicleId, typeFilter],
    enabled: isEnabled,
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      if (!vehicleId || !currentTenant?.id) {
        return { rows: [] as VehicleActivityAttachment[], total: 0, offset: 0 };
      }

      const { data, error } = await (supabase.rpc as any)('list_vehicle_attachments', {
        p_vehicle_id: vehicleId,
        p_type_filter: typeFilter,
        p_offset: pageParam,
        p_limit: pageSize,
        p_tenant_hint: currentTenant.id,
      });

      if (error) {
        logger.error('Erro ao buscar anexos do veículo:', error.message);
        throw error;
      }

      const rows = (data ?? []) as (VehicleActivityAttachment & { total_count: number })[];
      const total = rows[0]?.total_count ? Number(rows[0].total_count) : 0;

      const cleaned: VehicleActivityAttachment[] = rows.map(
        ({ total_count: _t, ...rest }) => rest,
      );

      return { rows: cleaned, total, offset: pageParam };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((acc, p) => acc + p.rows.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    staleTime: 30 * 1000,
  });

  const attachments = useMemo(
    () => query.data?.pages.flatMap((p) => p.rows) ?? [],
    [query.data],
  );

  const totalCount = query.data?.pages[0]?.total ?? 0;

  return {
    attachments,
    totalCount,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
    error: query.error,
  };
}
